/**
 * The dev session log: a pure reducer over gameplay-store transitions that
 * records each game's moves (time, options offered, choice, how it scored),
 * backtracks and result. Fed by a store subscription (useSessionRecorder),
 * so taps and auto-play moves are logged the same way.
 */
import { optionWords } from "../components/board/options";
import { gameModeOf, type GameMode } from "../utils/gameReportUtils";

export type MoveKind = "gold" | "purple" | "detour";

export type LogEntry =
  | {
      type: "move";
      t: number;
      from: string;
      choice: string;
      /** The next gold-path word the game scored the move against. */
      goldNext: string | null;
      options: string[];
      kind: MoveKind;
      streak: number;
      source: "tap" | "auto";
    }
  | { type: "backtrack"; t: number; to: string };

export interface GameLog {
  id: number;
  startedAt: number;
  start: string;
  target: string;
  mode: GameMode;
  par: number;
  entries: LogEntry[];
  result?: { status: "won" | "given_up"; t: number; moves: number };
}

interface Choice {
  playerPosition?: string;
  playerChose?: string;
  optimalChoice?: string;
  isGlobalOptimal: boolean;
  isLocalOptimal: boolean;
}

/** The slice of the gameplay store the log reads. */
export interface LogSnapshot {
  gameStatus: string;
  startWord: string | null;
  targetWord: string | null;
  currentWord: string | null;
  playerPath: string[];
  optimalPath: string[];
  optimalChoices: Choice[];
  streak: number;
  isTutorial: boolean;
  isDailyChallenge: boolean;
  isChallenge: boolean;
}

export interface LogContext {
  now: number;
  graph: Record<string, { edges?: Record<string, number> } | undefined> | null;
  source: "tap" | "auto";
}

export const MAX_GAMES = 5;

export const last = <T>(xs: readonly T[]): T | undefined => xs[xs.length - 1];

const isNewGame = (prev: LogSnapshot, next: LogSnapshot) =>
  next.gameStatus === "playing" &&
  !!next.startWord &&
  !!next.targetWord &&
  (prev.gameStatus !== "playing" ||
    prev.startWord !== next.startWord ||
    prev.targetWord !== next.targetWord ||
    (next.playerPath.length === 1 && prev.playerPath.length > 1));

const kindOf = (c: Choice): MoveKind =>
  c.isGlobalOptimal ? "gold" : c.isLocalOptimal ? "purple" : "detour";

export function logTransition(
  games: GameLog[],
  prev: LogSnapshot,
  next: LogSnapshot,
  ctx: LogContext,
): GameLog[] {
  if (isNewGame(prev, next)) {
    const game: GameLog = {
      id: (last(games)?.id ?? 0) + 1,
      startedAt: ctx.now,
      start: next.startWord as string,
      target: next.targetWord as string,
      mode: gameModeOf(next),
      par: Math.max(0, next.optimalPath.length - 1),
      entries: [],
    };
    return [...games, game].slice(-MAX_GAMES);
  }
  const game = last(games);
  if (!game || game.result) return games;
  let entries = game.entries;
  const n = next.optimalChoices.length;
  const p = prev.optimalChoices.length;
  if (n === p + 1) {
    const c = next.optimalChoices[n - 1];
    const from = c.playerPosition ?? "";
    entries = [
      ...entries,
      {
        type: "move",
        t: ctx.now,
        from,
        choice: c.playerChose ?? next.currentWord ?? "",
        goldNext: c.optimalChoice ?? null,
        options: optionWords(ctx.graph, from),
        kind: kindOf(c),
        streak: next.streak,
        source: ctx.source,
      },
    ];
  } else if (n < p && next.currentWord) {
    entries = [
      ...entries,
      { type: "backtrack", t: ctx.now, to: next.currentWord },
    ];
  }
  let result: GameLog["result"] = game.result;
  if (
    prev.gameStatus === "playing" &&
    (next.gameStatus === "won" || next.gameStatus === "given_up")
  ) {
    result = {
      status: next.gameStatus,
      t: ctx.now,
      moves: Math.max(0, next.playerPath.length - 1),
    };
  }
  if (entries === game.entries && result === game.result) return games;
  return [...games.slice(0, -1), { ...game, entries, result }];
}

/**
 * Open a log for the game already in progress when recording starts (the
 * panel mounts with the board, after the game has begun). Idempotent.
 */
export function joinGame(
  games: GameLog[],
  current: LogSnapshot,
  now: number,
): GameLog[] {
  const open = last(games);
  if (
    open &&
    !open.result &&
    open.start === current.startWord &&
    open.target === current.targetWord
  ) {
    return games;
  }
  const idle = { ...current, gameStatus: "idle" };
  return logTransition(games, idle, current, {
    now,
    graph: null,
    source: "tap",
  });
}

/** The longest run of Local Optimal (gold or purple) moves in a game. */
export function longestStreak(choices: readonly Choice[]): number {
  let best = 0;
  let run = 0;
  for (const c of choices) {
    run = c.isGlobalOptimal || c.isLocalOptimal ? run + 1 : 0;
    best = Math.max(best, run);
  }
  return best;
}
